import { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import './Announcements.css';

function NewAnnouncement() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');

  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [documentName, setDocumentName] = useState('');

  const [form, setForm] = useState({
    title: '',
    body: '',
    is_general: false,
    visible_from: '',
    visible_until: '',
    status: 'active',
    image: null,
    document: null,
  });

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/announcements/groups`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setGroups(res.data.data || []);
      } catch (error) {
        console.error('Fout bij ophalen groepen:', error);
        setGroups([]);
      }
    };

    fetchGroups();
  }, [token]);

  const handleGroupToggle = (groepId) => {
    setSelectedGroups((prev) =>
      prev.includes(groepId)
        ? prev.filter((id) => id !== groepId)
        : [...prev, groepId]
    );
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] || null;

    setForm((prev) => ({ ...prev, image: file }));

    if (file) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview(null);
    }
  };

  const handleRemoveSelectedImage = async () => {
    const result = await Swal.fire({
      title: 'Afbeelding verwijderen?',
      text: 'De geselecteerde afbeelding wordt verwijderd uit dit formulier.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ja, verwijderen',
      cancelButtonText: 'Annuleren',
      confirmButtonColor: '#dc3545',
    });

    if (!result.isConfirmed) return;

    setForm((prev) => ({ ...prev, image: null }));
    setImagePreview(null);

    const input = document.getElementById('announcementImageInput');
    if (input) input.value = '';
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files?.[0] || null;

    setForm((prev) => ({ ...prev, document: file }));
    setDocumentName(file?.name || '');
  };

  const handleRemoveSelectedDocument = async () => {
    const result = await Swal.fire({
      title: 'Document verwijderen?',
      text: 'Het geselecteerde document wordt verwijderd uit dit formulier.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ja, verwijderen',
      cancelButtonText: 'Annuleren',
      confirmButtonColor: '#dc3545',
    });

    if (!result.isConfirmed) return;

    setForm((prev) => ({ ...prev, document: null }));
    setDocumentName('');

    const input = document.getElementById('announcementDocumentInput');
    if (input) input.value = '';
  };

  const validateForm = async () => {
    if (!form.title.trim()) {
      await Swal.fire('Verplicht', 'Titel is verplicht.', 'warning');
      return false;
    }

    if (!form.visible_from) {
      await Swal.fire('Verplicht', 'Zichtbaar vanaf is verplicht.', 'warning');
      return false;
    }

    if (!form.visible_until) {
      await Swal.fire('Verplicht', 'Zichtbaar t/m is verplicht.', 'warning');
      return false;
    }

    if (form.visible_until < form.visible_from) {
      await Swal.fire(
        'Ongeldige periode',
        'De einddatum mag niet vóór de startdatum liggen.',
        'warning'
      );
      return false;
    }

    if (!form.is_general && selectedGroups.length === 0) {
      await Swal.fire(
        'Groepen ontbreken',
        'Kies minimaal één groep of selecteer algemeen voor iedereen.',
        'warning'
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validateForm();
    if (!isValid) return;

    try {
      const payload = new FormData();

      payload.append('title', form.title.trim());
      payload.append('body', form.body || '');
      payload.append('is_general', String(form.is_general));
      payload.append('visible_from', form.visible_from);
      payload.append('visible_until', form.visible_until);
      payload.append('status', form.status);
      payload.append('groep_ids', JSON.stringify(selectedGroups));

      if (form.image) {
        payload.append('image', form.image);
      }

      if (form.document) {
        payload.append('document', form.document);
      }

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/announcements`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      await Swal.fire({
        toast: true,
        position: 'top',
        icon: 'success',
        title: 'Mededeling aangemaakt',
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      });

      navigate('/announcements');
    } catch (error) {
      console.error('Fout bij aanmaken mededeling:', error);

      await Swal.fire({
        icon: 'error',
        title: 'Opslaan mislukt',
        text:
          error.response?.data?.message ||
          'Er is iets misgegaan bij het opslaan van de mededeling.',
      });
    }
  };

  return (
    <div className="announcements-page">
      <div className="bg-white p-4">
        <h2 className="text-center">Nieuwe mededeling</h2>
        <hr className="seperator" />

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">
              Titel <span className="text-danger">*</span>
            </label>
            <input
              className="form-control rounded-0"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Berichttekst</label>
            <textarea
              className="form-control rounded-0"
              rows="5"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
            />
          </div>

          <div className="row">
            <div className="mb-3 col-md-6">
              <label className="form-label">
                Zichtbaar vanaf <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control rounded-0"
                value={form.visible_from}
                onChange={(e) =>
                  setForm({ ...form, visible_from: e.target.value })
                }
                required
              />
            </div>

            <div className="mb-3 col-md-6">
              <label className="form-label">
                Zichtbaar t/m <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control rounded-0"
                value={form.visible_until}
                onChange={(e) =>
                  setForm({ ...form, visible_until: e.target.value })
                }
                required
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Status</label>
            <select
              className="form-select rounded-0"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="active">Actief</option>
              <option value="inactive">Inactief</option>
            </select>
          </div>

          <div className="mb-3 form-check">
            <input
              id="isGeneralAnnouncement"
              type="checkbox"
              className="form-check-input"
              checked={form.is_general}
              onChange={(e) =>
                setForm({ ...form, is_general: e.target.checked })
              }
            />
            <label className="form-check-label" htmlFor="isGeneralAnnouncement">
              Algemeen voor iedereen
            </label>
          </div>

          {!form.is_general && (
            <div className="mb-3">
              <label className="form-label">Koppel aan groepen</label>

              <div className="announcement-group-box">
                {groups.length === 0 ? (
                  <p className="text-muted mb-0">Geen groepen gevonden.</p>
                ) : (
                  groups.map((group) => (
                    <div key={group.groep_id} className="form-check">
                      <input
                        id={`group-${group.groep_id}`}
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedGroups.includes(group.groep_id)}
                        onChange={() => handleGroupToggle(group.groep_id)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`group-${group.groep_id}`}
                      >
                        {group.groep_name}
                        {group.school_year ? ` — ${group.school_year}` : ''}
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="form-label">Afbeelding</label>

            <input
              id="announcementImageInput"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="form-control rounded-0"
              onChange={handleImageChange}
            />

            {imagePreview && (
              <>
                <div className="announcement-image-preview mt-3">
                  <img src={imagePreview} alt="Voorbeeld" />
                </div>

                <div className="mt-2 d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={handleRemoveSelectedImage}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Verwijder afbeelding
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="mb-4">
            <label className="form-label">Document</label>

            <input
              id="announcementDocumentInput"
              type="file"
              accept=".pdf,.doc,.docx"
              className="form-control rounded-0"
              onChange={handleDocumentChange}
            />

            {documentName && (
              <div className="mt-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                <span className="announcement-file-link">
                  <i className="bi bi-paperclip me-1"></i>
                  {documentName}
                </span>

                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleRemoveSelectedDocument}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Verwijder document
                </button>
              </div>
            )}
          </div>

          <hr className="seperator" />

          <div className="row">
            <div className="col-md-6 mb-2">
              <button
                type="button"
                className="btn btn-secondary w-100 rounded-0"
                onClick={() => navigate('/announcements')}
              >
                Annuleren
              </button>
            </div>

            <div className="col-md-6 mb-2">
              <button type="submit" className="btn btn-primary w-100 rounded-0">
                Opslaan
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NewAnnouncement;